import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { unauthenticate } from '../../data/providers/userProvider';
import { Column } from '../layout/Column';
import { MainGrid } from '../layout/MainGrid';
import { Brand } from '../shared/Brand';
import { Card } from '../shared/card/Card';
import { Icon } from '../shared/Icon';

const AccessPending = (): React.ReactElement => {
  const [isLoggingOut, setIsLoggingOut] = React.useState<boolean>(false);

  const handleLogout = async (): Promise<void> => {
    setIsLoggingOut(true);
    try {
      await unauthenticate();
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if logout fails
      window.location.href = '/';
    }
  };

  return (
    <PageWrapper>
      <CenteredContent>
        <LogoContainer>
          <Brand alt='Hawkin Dynamics Logo' />
        </LogoContainer>
        <AnimatedCard>
          <CardColumn>
            <StyledIcon name='admin_panel_settings' size='x-large' />
            <Title>Access Pending</Title>
            <Message>
              You have not been granted access to any teams in this organization.
              Please contact your organization administrator.
            </Message>
            <LogoutButton onClick={handleLogout} disabled={isLoggingOut}>
              <Icon name='logout' size='small' style={{ marginRight: '8px' }} />
              {isLoggingOut ? 'Logging out...' : 'Log Out'}
            </LogoutButton>
          </CardColumn>
        </AnimatedCard>
      </CenteredContent>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-surface-1);
  overflow: hidden;
`;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  max-width: 600px;
  padding: 24px;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 16px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    transform: scale(0.8);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const AnimatedCard = styled(Card)`
  animation: ${slideUp} 0.4s ease-out;
  max-width: 600px;
  margin: auto;
`;

const CardColumn = styled(Column)`
  align-items: center;
  justify-content: center;
  padding: 64px 48px;

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

const StyledIcon = styled(Icon)`
  color: var(--color-primary);
  margin-bottom: 32px;
  font-size: 80px;

  @media (max-width: 768px) {
    font-size: 60px;
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 16px;
  color: var(--color-text-primary);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

const Message = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 40px;
  color: var(--color-text-secondary);
  text-align: center;
  max-width: 500px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 32px;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  background-color: var(--color-primary);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export { AccessPending };
